import { useEffect } from "react";

export interface ConfirmDialogProps {
  open: boolean;
  title: string;
  message: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** "danger" styles the confirm button for destructive actions. */
  tone?: "danger" | "primary";
  busy?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

/**
 * A small accessible confirmation modal used before any destructive admin
 * action (delete, reject, refund …). It shows nothing when `open` is false and
 * closes on Escape or a backdrop click, so it never leaves a stuck overlay.
 */
export default function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  tone = "danger",
  busy = false,
  onConfirm,
  onCancel,
}: ConfirmDialogProps) {
  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !busy) onCancel();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, busy, onCancel]);

  if (!open) return null;

  const confirmClass =
    tone === "danger"
      ? "bg-secondary-500 text-background-50 hover:bg-secondary-600 dark:text-foreground-950"
      : "bg-primary-500 text-background-50 hover:bg-primary-600 dark:text-foreground-950";

  return (
    <div
      className="fixed inset-0 z-[120] flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      aria-label={title}
    >
      <button
        type="button"
        aria-label="Close dialog"
        onClick={() => !busy && onCancel()}
        className="absolute inset-0 bg-foreground-950/40 cursor-default"
      ></button>
      <div className="relative w-full max-w-md rounded-lg border border-background-200 bg-background-50 p-6">
        <div className="flex items-start gap-3">
          <span
            className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-lg ${
              tone === "danger" ? "bg-secondary-100 text-secondary-700" : "bg-primary-100 text-primary-700"
            }`}
          >
            <i className={tone === "danger" ? "ri-error-warning-line text-xl" : "ri-question-line text-xl"}></i>
          </span>
          <div className="min-w-0">
            <h2 className="font-heading text-base font-bold text-foreground-950">{title}</h2>
            <p className="mt-1.5 text-sm text-foreground-600">{message}</p>
          </div>
        </div>

        <div className="mt-6 flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="inline-flex items-center justify-center rounded-md border border-background-300 bg-background-50 px-4 py-2.5 text-sm font-semibold text-foreground-800 hover:border-primary-400 disabled:opacity-60 whitespace-nowrap cursor-pointer"
          >
            {cancelLabel}
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={busy}
            className={`inline-flex items-center justify-center gap-2 rounded-md px-4 py-2.5 text-sm font-bold disabled:opacity-60 whitespace-nowrap cursor-pointer ${confirmClass}`}
          >
            {busy && <i className="ri-loader-4-line animate-spin"></i>}
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}